import {PctDouble, Seconds} from '@store/@types/util';
import React, {useCallback, useEffect} from 'react';
import './CallSpeaker.sass';
import {registerEvents} from 'utils/registerEvents';
import {SeekHelperTooltip} from '@components/call-card/SeekHelperTooltip';
import {CallTick} from './CallTick';
import {SetState} from '@store/@types/util/React';
import {DragStatus} from '@store/call-card/CallCard.types';
import clamp from 'lodash/clamp';

// Min # of pixels before we start rendering a comment highlight while dragging
// Helps avoid accidentally creating very small highlights
const MIN_DRAG_SIZE = 3;

interface SeekTrackProps {
  setTimePct: (pct: PctDouble) => void;
  duration: Seconds;
  currentTime: Seconds;
  trackRef: HTMLDivElement | null;
  showPlayerTick?: boolean;
  setDragStatus: SetState<DragStatus>;
  dragStatus: DragStatus;
  // Determines whether we should update position for speaker track component
  // Very janky, and I don't like it. Need to find some better way to handle just
  // seeking vs seeking and updating speaker track comment region
  commentModal: boolean;
}

/**
 * Handles seeking on the player track, as well as managing state for
 * @link TrackHighlight
 */
export const SeekTrack: React.FC<SeekTrackProps> = ({
  currentTime,
  duration,
  setTimePct,
  trackRef = null,
  showPlayerTick = false,
  setDragStatus,
  dragStatus,
  commentModal,
}) => {
  // Update drag status based on cursor location relative to the player track
  const updateDragStatus = useCallback(
    (xPos: number) => {
      if (!trackRef) return null;
      const {left, width} = trackRef.getBoundingClientRect();
      const newPos = xPos - left;

      // set isDragging to false if we move out of bounds
      const isDragging = newPos >= 0 && newPos <= width;

      setDragStatus(s => {
        return Math.abs(s.startPos! - newPos) > MIN_DRAG_SIZE
          ? {...s, isDragging, endPos: commentModal ? null : newPos}
          : s;
      });
    },
    [setDragStatus, trackRef, commentModal]
  );

  // Update time on drag movement
  useEffect(() => {
    if (!dragStatus.isDragging) return; // no need for this handler if not dragging
    const onMouseMove = e => updateDragStatus(e.clientX);
    return registerEvents(document, [['mousemove', onMouseMove]]);
  }, [dragStatus.isDragging, updateDragStatus]);

  // Handle starting and stopping drag seeking
  useEffect(() => {
    if (!trackRef) return;

    const finishDrag = e => {
      setDragStatus(s => {
        // If we've clicked the track, but havent dragged yet, then update time and reset dragStatus
        if (!!s.startPos && s.endPos === null) {
          const {left, width} = trackRef.getBoundingClientRect();
          const newPos = e.clientX - left;
          setTimePct(clamp(newPos / width, 0, 1));
          return {startPos: null, endPos: null, isDragging: false};
        } else {
          return {...s, isDragging: false};
        }
      });
    };
    const beginDrag = e => {
      const {left} = trackRef.getBoundingClientRect();
      const startPos = e.clientX - left; // cursor position, minus left offset
      if (commentModal) {
        setDragStatus({isDragging: true, startPos: null, endPos: null});
      } else {
        setDragStatus({isDragging: true, endPos: null, startPos: startPos});
      }
      updateDragStatus(e.clientX);
    };
    const addComment = e => {
      const {left} = trackRef.getBoundingClientRect();
      const startPos = e.clientX - left; // cursor position, minus left offset
      if (commentModal) {
        setDragStatus({isDragging: false, startPos: null, endPos: null});
      } else {
        setDragStatus({isDragging: false, endPos: startPos, startPos: startPos});
      }
    };

    // document listener so user can drag above or below of the track
    document.addEventListener('mouseup', finishDrag);
    trackRef.addEventListener('mousedown', beginDrag);
    trackRef.addEventListener('dblclick', addComment);

    return () => {
      document.removeEventListener('mouseup', finishDrag);
      trackRef.removeEventListener('mousedown', beginDrag);
      trackRef.removeEventListener('dblclick', addComment);
    };
  }, [setDragStatus, updateDragStatus, trackRef, commentModal, setTimePct]);

  if (!trackRef) return null;

  return (
    <>
      {showPlayerTick && <CallTick duration={duration} location={currentTime} type="player" />}
      <SeekHelperTooltip trackRef={trackRef} duration={duration} />
    </>
  );
};
